import { Box, LinearProgress, Tooltip, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useEffect, useState } from "react";

import { OrganizationTemplateRubric } from "features/Influencer/ContactList/schema";
import {
  calculateScore,
  getScoreColor,
  getScoreLabel,
} from "features/Influencer/Templates/TemplatesTable/helpers";

const LOADING_MESSAGES = [
  "Reading through your template...",
  "Crunching out suggestions...",
  "Analyzing your pitch style...",
  "Checking for personalization...",
  "Evaluating tone and clarity...",
  "Almost there, hold tight...",
];

interface Props {
  rubric: OrganizationTemplateRubric | null;
  loading: boolean;
  readyToGo?: boolean;
}

const HalfDoughnut = ({ rubric, loading, readyToGo = false }: Props) => {
  const size = 260;
  const strokeWidth = 20;
  const radius = 110;
  const cx = size / 2;
  const cy = size / 2;

  const startAngle = 180;
  const totalAngle = 180;

  const toRadians = (angle: number) => (angle * Math.PI) / 180;
  const getCoords = (angle: number) => ({
    x: cx + radius * Math.cos(toRadians(angle)),
    y: cy + radius * Math.sin(toRadians(angle)),
  });

  const start = getCoords(startAngle);
  const bgEnd = getCoords(360);

  const [messageIndex, setMessageIndex] = useState(0);
  const value = rubric ? calculateScore(rubric) : 0;

  useEffect(() => {
    if (!loading) return;

    const messageInterval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
    }, 1800);

    return () => {
      clearInterval(messageInterval);
    };
  }, [loading]);

  const scoreAngle =
    rubric?.userScore === null
      ? startAngle + totalAngle / 2
      : startAngle + (totalAngle * value) / 100;

  const scoreEnd = getCoords(scoreAngle);
  const largeArc = scoreAngle - startAngle > 180 ? 1 : 0;

  const bgPath = `M ${start.x} ${start.y} A ${radius} ${radius} 0 1 1 ${bgEnd.x} ${bgEnd.y}`;
  const scorePath =
    value > 0
      ? `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${scoreEnd.x} ${scoreEnd.y}`
      : "";

  const colors =
    value > 0 ? getScoreColor(value) : { filled: grey[400], track: grey[100] };

  const textAreaHeight = 64;
  const containerHeight = cy + textAreaHeight;

  if (!loading && !rubric) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
        <Box sx={{ position: "relative", width: 200, height: 110 }}>
          <svg
            viewBox="0 0 200 110"
            width="200"
            height="110"
            style={{ display: "block" }}
          >
            <path
              d="M 20 100 A 80 80 0 0 1 180 100"
              fill="none"
              stroke="#f2f3fe"
              strokeWidth={14}
              strokeLinecap="round"
              strokeDasharray="1.5 14"
            />
          </svg>
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              top: 60,
              transform: "translateX(-50%)",
              width: 46,
              height: 46,
              borderRadius: "50%",
              bgcolor: "#014638",
              color: "#F7EE6F",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 20px rgba(1, 70, 56, 0.22)",
            }}
          >
            <Box
              component="i"
              className="fa-solid fa-sparkles"
              sx={{ fontSize: 20 }}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          py: 4,
          px: 3,
          minHeight: containerHeight,
        }}
      >
        <LinearProgress
          sx={{
            width: "100%",
            maxWidth: size,
            height: 8,
            borderRadius: 4,
            backgroundColor: grey[200],
            "& .MuiLinearProgress-bar": { borderRadius: 4 },
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: grey[600],
            textAlign: "center",
            minHeight: "1.5em",
            transition: "opacity 0.4s ease",
          }}
        >
          {LOADING_MESSAGES[messageIndex]}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Box sx={{ position: "relative", width: size, height: containerHeight }}>
        <svg
          width={size}
          height={size}
          style={{ position: "absolute", top: 0, left: 0 }}
        >
          {/* Background track */}
          <path
            d={bgPath}
            fill="none"
            stroke={colors.track}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            style={{ transition: "stroke 0.4s ease" }}
          />
          {/* Filled arc */}
          {scorePath && (
            <path
              d={scorePath}
              fill="none"
              stroke={colors.filled}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              style={{ transition: "stroke 0.4s ease" }}
            />
          )}
        </svg>

        {/* Score + message centered in the arc opening */}
        <Box
          sx={{
            position: "absolute",
            top: cy - 50,
            left: "50%",
            transform: "translateX(-50%)",
            textAlign: "center",
            width: radius * 1.4,
          }}
        >
          {readyToGo ? (
            <>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1,
                  color: getScoreColor(value).text,
                }}
              >
                {rubric?.userScore}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: grey[600] }}>
                out of {rubric?.totalScore}
              </Typography>
            </>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 0.5,
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1,
                    color: colors.filled,
                    transition: "color 0.3s ease",
                  }}
                >
                  {value === 0 ? "??" : rubric?.userScore}
                </Typography>
                <Typography variant="caption" sx={{ color: grey[500] }}>
                  / {rubric?.totalScore}
                </Typography>
                {value > 0 && (
                  <Tooltip
                    title="Note: This feedback is based on general rules of thumbs and is meant to provide general guidance. If you have a template with a high response rate, you may not need to change a thing!"
                    placement="top"
                    arrow
                  >
                    <Box
                      component="span"
                      className="fa-solid fa-circle-info"
                      sx={{
                        fontSize: 10,
                        color: grey[400],
                        cursor: "default",
                        alignSelf: "center",
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: grey[600],
                  minHeight: "2.5em",
                  transition: "opacity 0.3s ease",
                }}
              >
                {value === 0
                  ? "Find out if your template is ready to send 👇"
                  : getScoreLabel(value)}
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default HalfDoughnut;
